/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 그리드의 날짜 필터 클래스를 정의한다.
 *
 * @file /src/models/table/filters/Date.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 6. 2.
 */
import * as parent from './Filter';
import { Date as DateField } from '@components/form/field/date/Date';
import { Select } from '@components/form/field/select/Select';
import { Container } from '@components/form/field/container/Container';
import { Local } from '@models/store/list/Local';
import { Language } from '@utils/Language';

export interface Properties extends parent.Properties {
    /**
     * 필터타입
     */
    type: 'date';

    /**
     * 입력폼에 표시할 날짜포맷
     */
    displayFormat?: string;

    /**
     * 필터에 실제 적용할 날짜포맷
     */
    format?: 'timestamp' | 'date' | 'datetime';
}

export class Date extends parent.Filter {
    /**
     * 필터타입
     */
    type: string = 'date';

    /**
     * 입력폼에 표시할 날짜포맷
     */
    displayFormat: string;

    /**
     * 필터에 실제 적용할 날짜포맷
     */
    format: 'timestamp' | 'date' | 'datetime';

    /**
     * 비교연산자 필드
     */
    operatorField: Select = null;

    /**
     * 값 필드
     */
    valueField: DateField = null;

    /**
     * 시작값 필드
     */
    startValueField: DateField = null;

    /**
     * 종료값 필드
     */
    endValueField: DateField = null;

    /**
     * 날짜 필터를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties) {
        super(properties);

        this.displayFormat = properties?.displayFormat ?? 'Y-m-d';
        this.format = properties?.format ?? 'date';
    }

    /**
     * 필터필드를 가져온다.
     *
     * @returns 필드
     */
    getField(): Container {
        if (this.field === null) {
            this.field = new Container({
                direction: 'column',
                items: [
                    this.#getOperatorField(),
                    this.#getValueField(),
                    this.#getStartValueField(),
                    this.#getEndValueField(),
                ],
            });
        }
        return this.field as Container;
    }

    /**
     * 필터객체를 가져온다.
     *
     * @returns 필터객체
     */
    async getFilter(): Promise<{ value: any; operator: string } | null> {
        const operator = (await this.#getOperatorField().getValue()) as string;
        const timezone = new globalThis.Date().getTimezoneOffset() * 60;

        if (operator == '=' || operator == '>=' || operator == '<=') {
            const value = await this.#getValueField().getValue();
            if (value === null) {
                return null;
            }
            return {
                value: { operator: operator, value: value, format: this.format, timezone: timezone },
                operator: 'date',
            };
        }

        if (operator == 'range') {
            const startValue = await this.#getStartValueField().getValue();
            const endValue = await this.#getEndValueField().getValue();
            if (startValue === null || endValue === null) {
                return null;
            }
            return {
                value: { operator: 'range', range: [startValue, endValue], format: this.format, timezone: timezone },
                operator: 'date',
            };
        }

        return { value: { operator: operator, format: this.format, timezone: timezone }, operator: 'date' };
    }

    /**
     * 현재 필터설정을 필터필드에 적용한다.
     *
     * @param filter - 필터객체
     */
    async setFilter(filter: { value: any; operator: string } | null): Promise<void> {
        this.getButton().setDisabled(true);

        if (filter === null) {
            await this.#getOperatorField().setValue('today');
            await this.#getValueField().setValue(null);
            await this.#getStartValueField().setValue(null);
            await this.#getEndValueField().setValue(null);
        } else {
            const inner = filter.value ?? {};
            const operator = inner.operator ?? 'today';
            await this.#getOperatorField().setValue(operator);

            if (operator == '=' || operator == '>=' || operator == '<=') {
                await this.#getValueField().setValue(inner.value ?? null);
            } else if (operator == 'range') {
                await this.#getStartValueField().setValue(inner.range?.[0] ?? null);
                await this.#getEndValueField().setValue(inner.range?.[1] ?? null);
            }
        }

        this.getButton().setDisabled(false);
    }

    /**
     * 비교연산자 필드를 가져온다.
     *
     * @returns 비교연산자 필드
     */
    #getOperatorField(): Select {
        if (this.operatorField === null) {
            this.operatorField = new Select({
                store: new Local({
                    provider: {
                        dataFields: ['value', 'display'],
                        loader: async () => [
                            { value: 'today', display: 'today' },
                            { value: 'yesterday', display: 'yesterday' },
                            { value: 'this_week', display: 'this_week' },
                            { value: 'last_week', display: 'last_week' },
                            { value: 'this_month', display: 'this_month' },
                            { value: 'last_month', display: 'last_month' },
                            { value: 'this_year', display: 'this_year' },
                            { value: 'last_year', display: 'last_year' },
                            { value: '=', display: 'equal' },
                            { value: '>=', display: 'gte' },
                            { value: '<=', display: 'lte' },
                            { value: 'range', display: 'range' },
                        ],
                    },
                }),
                value: 'today',
                listRenderer: async (display) => {
                    return await Language.get('models.table.filter.date_operator.' + display).getText();
                },
                renderer: async (display) => {
                    return await Language.get('models.table.filter.date_operator.' + display).getText();
                },
                listeners: {
                    change: async (_field, value) => {
                        if (value == '=' || value == '>=' || value == '<=') {
                            this.#getValueField().show();
                            this.#getStartValueField().hide();
                            this.#getEndValueField().hide();
                        } else if (value == 'range') {
                            this.#getValueField().hide();
                            this.#getStartValueField().show();
                            this.#getEndValueField().show();
                        } else {
                            this.#getValueField().hide();
                            this.#getStartValueField().hide();
                            this.#getEndValueField().hide();
                        }
                    },
                },
            });
        }
        return this.operatorField;
    }

    /**
     * 값 필드를 가져온다.
     *
     * @returns 값 필드
     */
    #getValueField(): DateField {
        if (this.valueField === null) {
            this.valueField = new DateField({
                hidden: true,
                displayFormat: this.displayFormat,
                format: 'Y-m-d',
            });
        }
        return this.valueField;
    }

    /**
     * 시작값 필드를 가져온다.
     *
     * @returns 시작값 필드
     */
    #getStartValueField(): DateField {
        if (this.startValueField === null) {
            this.startValueField = new DateField({
                hidden: true,
                label: Language.get('models.table.filter.date_start'),
                labelPosition: 'top',
                displayFormat: this.displayFormat,
                format: 'Y-m-d',
            });
        }
        return this.startValueField;
    }

    /**
     * 종료값 필드를 가져온다.
     *
     * @returns 종료값 필드
     */
    #getEndValueField(): DateField {
        if (this.endValueField === null) {
            this.endValueField = new DateField({
                hidden: true,
                label: Language.get('models.table.filter.date_end'),
                labelPosition: 'top',
                displayFormat: this.displayFormat,
                format: 'Y-m-d',
            });
        }
        return this.endValueField;
    }
}
